import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import Tooltip from '@mui/material/Tooltip'
import { makeStyles } from '@mui/styles'
import PopperMenu from '@/modules/base/components/popper-menu/popper-menu'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import theme from '@/theme'
import DEBOUNCE_TIMES from '@/modules/base/components/buttons/debounce-times'
import loaderIcon from '@assets/images/icons/loader.svg'
import LoaderInline from '@/modules/base/components/loader/loader-inline'

export const VARIANTS = Object.freeze({
  primary: 'primary',
  white: 'white',
  red: 'red',
  grey: 'grey',
  outline: 'outline',
  icon: 'icon',
})

export const SIZES = Object.freeze({
  smaller: 'smaller',
  small: 'small',
  medium: 'medium',
  mLarge: 'mLarge',
  large: 'large',
})

const allowedSizes = [SIZES.smaller, SIZES.small, SIZES.medium, SIZES.mLarge, SIZES.large]
const iconSize = {
  smaller: '10px',
  small: '12px',
  medium: '15px',
  mLarge: '15px',
  large: '15px',
}

const useStyles = makeStyles((theme) => ({
  indicator: {
    position: 'absolute',
    width: 17,
    height: 17,
    backgroundColor: theme.primary,
    color: theme.white,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    top: 5,
    right: 5,
    paddingRight: 1,
    fontSize: 12,
    paddingTop: 1,
  },
  disabled: {
    pointerEvents: 'none',
  },
  tooltip: {
    fontSize: theme.fontSizes.tooltip,
  },
  spinnerIcon: ({ size }) => ({
    justifyContent: 'center',
    height: size === SIZES.medium ? 30 : 45,
    minWidth: size === SIZES.medium ? 30 : 45,
    maxWidth: 45,
    padding: 0,
    fill: theme.buttonColor,
    background: theme.backgroundColorIconDisabled,
    alignItems: 'center',
    '& span': {
      marginTop: 0,
      display: 'flex',
    },
    '& svg': {
      width: '18px !important',
      height: '18px !important',
    },
    borderRadius: size === SIZES.medium ? 'inherit' : 5,
  }),
  button: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    outline: 'none',
    border: theme.border,
    borderRadius: theme.borderRadius,
    background: theme.backgroundColor,
    padding: 0,
    '&:hover:not(:disabled)': {
      background: theme.white,
      transition: 'all 0.2s ease-out',
    },
    '&:disabled': {
      cursor: 'default',
      background: theme.white,
      '& svg': {
        fill: theme.disabledColor,
      },
    },
    '&:focus-visible': {
      borderColor: theme.Input.borderColorActive,
    },
  },
  primary: {
    background: theme.colorPrimaryButtonBackground,
    '&:hover:not(:disabled)': {
      background: theme.colorPrimaryButtonBackgroundHover,
    },
  },
  white: {
    background: theme.white,
    '&:hover:not(:disabled)': {
      background: theme.backgroundColor,
    },
  },
  red: {
    background: theme.colorDeleteButtonBackground,
    '&:hover:not(:disabled)': {
      background: theme.colorDeleteButtonBackgroundHover,
    },
  },
  grey: {
    background: theme.backgroundColor,
    '&:hover:not(:disabled)': {
      background: theme.white,
    },
  },
  outline: {
    background: theme.buttonColor,
    '&:hover:not(:disabled)': {
      background: theme.lighterGrey,
    },
    '&:disabled': {
      borderColor: theme.disabledColor,
    },
  },
  icon: {
    border: 'none',
    borderRadius: '50%',
    background: 'transparent',
    '&:hover:not(:disabled)': {
      background: 'transparent',
    },
    '&:disabled': {
      background: 'transparent',
    },
  },
  smaller: {
    minHeight: 15,
    maxHeight: 15,
    minWidth: 15,
    maxWidth: 15,
  },
  small: {
    minHeight: 20,
    maxHeight: 20,
    minWidth: 20,
    maxWidth: 20,
  },
  medium: {
    minHeight: 32,
    maxHeight: 32,
    minWidth: 32,
    maxWidth: 32,
  },
  mLarge: {
    minHeight: 40,
    maxHeight: 40,
    minWidth: 40,
    maxWidth: 40,
  },
  large: {
    minHeight: 45,
    maxHeight: 45,
    minWidth: 45,
    maxWidth: 45,
  },
  fadeIn: {
    opacity: 1,
    transition: 'opacity 0.4s',
    width: 'auto',
  },
  fadeOut: {
    opacity: 0,
    pointerEvents: 'none',
    transition: 'opacity 0.4s',
    width: 0,
    height: 0,
    minWidth: 0,
    minHeight: 0,
    maxWidth: 0,
    maxHeight: 0,
    padding: 0,
    margin: 0,
    border: 'none',
    overflow: 'hidden',
  },
}))

const SquareIconButton = props => {
  const {
    id,
    size = 'medium',
    icon,
    iconColor = theme.iconFillColor,
    isMuiIcon = false,
    onClick,
    customClass,
    className,
    disabled,
    tooltip = '',
    tooltipIgnoreDisabled = false,
    type = 'button',
    classes,
    variant,
    popperStyle = {},
    autoClosePopper,
    children,
    customIconSize,
    tabIndex,
    title,
    dataQsId,
    debounceTime = DEBOUNCE_TIMES.OFF,
    indicator,
    popperNoArrow,
    isLoading,
    loaderClass,
    buttonWrapperClass,
    secret = false,
    ariaLabel,
    ariaHasPopup,
    ariaExpanded,
    ariaControls,
  } = props

  const styles = useStyles({ size })
  const buttonRef = useRef()
  const [popperOpen, setPopperOpen] = useState(false)
  const [clickDebounce, setClickDebounce] = useState(false)
  const [isShiftPressed, setIsShiftPressed] = useState(false)
  const onClickRef = useRef()
  onClickRef.current = onClick
  const clickDebounceTimeOutRef = useRef()

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Shift') setIsShiftPressed(true)
    }
    const handleKeyUp = (e) => {
      if (e.key === 'Shift') setIsShiftPressed(false)
    }
    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('keyup', handleKeyUp)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('keyup', handleKeyUp)
      clearTimeout(clickDebounceTimeOutRef.current)
    }
  }, [])

  const onClickHandle = useCallback((e) => {
    if (!clickDebounce) {
      if (debounceTime > 0) {
        setClickDebounce(true)
      }
      onClickRef.current && onClickRef.current(e)
      if (secret) {
        setIsShiftPressed(false)
      }
      if (debounceTime > 0) {
        clickDebounceTimeOutRef.current = setTimeout(() => {
          setClickDebounce(false)
        }, debounceTime)
      }
    }
  }, [debounceTime, secret])

  const handleListKeyDown = useCallback((event) => {
    if (event.key === 'Tab') {
      event.preventDefault()
      setPopperOpen(false)
    } else if (event.key === 'Escape') {
      setPopperOpen(false)
    }
  }, [])

  // Secret logic: hide if secret and not isShiftPressed, fade in if secret and isShiftPressed, always show if not secret
  let showButton = true
  let fadeClass = ''
  if (secret) {
    showButton = true // always render for transition, but control opacity
    fadeClass = isShiftPressed ? styles.fadeIn : styles.fadeOut
  }

  const renderIcon = () => {
    if (isMuiIcon) {
      return icon
    }
    return (
      <SVGIcon
        width={customIconSize || iconSize[size]}
        height={customIconSize || iconSize[size]}
        svg={icon}
        fill={iconColor}
      />
    )
  }

  const buttonContent = (
    <>
      <Tooltip
        title={tooltip}
        disableHoverListener={!tooltip || (disabled && !tooltipIgnoreDisabled)}
        classes={{
          tooltip: classes?.tooltip || styles.tooltip,
        }}>
        <div className={buttonWrapperClass}>
          <button
            id={id}
            title={title}
            className={clsx(
              styles.button,
              styles[size],
              variant && styles[variant],
              customClass,
              className,
              { [styles.disabled]: disabled },
              fadeClass,
            )}
            ref={buttonRef}
            disabled={disabled || (clickDebounce && onClick != null)}
            onClick={(e) => {
              setPopperOpen(!popperOpen)
              onClick && onClickHandle(e)
            }}
            type={type}
            tabIndex={tabIndex}
            data-qs-id={dataQsId}
            aria-label={ariaLabel}
            aria-haspopup={ariaHasPopup}
            aria-expanded={ariaExpanded}
            aria-controls={ariaControls}
          >
            {indicator ? <span className={styles.indicator}>{indicator}</span> : null}
            {clickDebounce || isLoading
              ? <LoaderInline customClass={clsx(styles.spinnerIcon, loaderClass)} visible={true} customIcon={loaderIcon} customAnimation='spin 450ms linear infinite' width={''} height={''} />
              : renderIcon()
            }
          </button>
        </div>
      </Tooltip>
      { children
        && <PopperMenu
          arrow={!popperNoArrow}
          open={popperOpen}
          elementRef={buttonRef}
          style={popperStyle}
          disablePortal={true}
          onClick={() => autoClosePopper && setPopperOpen(false)}
          onClose={() => setPopperOpen(false)}>
          <div onKeyDown={handleListKeyDown} >{children}</div>
        </PopperMenu>
      }
    </>
  )

  // If not secret, always show. If secret, only show (with fade) if isShiftPressed
  if (!secret || (secret && showButton)) {
    return buttonContent
  } else {
    return null
  }
}

SquareIconButton.propTypes = {
  className: PropTypes.string,
  buttonWrapperClass: PropTypes.string,
  tooltip: PropTypes.string,
  icon: PropTypes.oneOfType([PropTypes.elementType, PropTypes.element]).isRequired,
  isMuiIcon: PropTypes.bool,
  iconColor: PropTypes.string,
  size: PropTypes.oneOf(allowedSizes),
  variant: PropTypes.oneOf([...Object.values(VARIANTS)]),
  disabled: PropTypes.bool,
  tooltipIgnoreDisabled: PropTypes.bool,
  popperStyle: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  customIconSize: PropTypes.string,
  title: PropTypes.string,
  tabIndex: PropTypes.number,
  onClick: PropTypes.func,
  customClass: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  debounceTime: PropTypes.number,
  indicator: PropTypes.string,
  popperNoArrow: PropTypes.bool,
  isLoading: PropTypes.bool,
  loaderClass: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  children: PropTypes.node,
  secret: PropTypes.bool,
  dataQsId: PropTypes.string.isRequired,
  autoClosePopper: PropTypes.bool,
  ariaLabel: PropTypes.string,
  ariaHasPopup: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  ariaExpanded: PropTypes.bool,
  ariaControls: PropTypes.string,
}

export default SquareIconButton
